"use client";

import { motion } from "framer-motion";
import { BookOpen, Briefcase, Building, Home, PartyPopper, type LucideIcon } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

/** Icon-Schlüssel, damit Server-Seiten keine Komponenten-Referenzen übergeben müssen. */
const ICONS = {
  portrait: BookOpen,
  campus: Building,
  life: PartyPopper,
  career: Briefcase,
  home: Home,
} satisfies Record<string, LucideIcon>;

export type InfoSectionIcon = keyof typeof ICONS;

/**
 * Generische Text-Sektion der Detailseite („Über die Universität",
 * „Campus & Ausstattung", „Studentenleben & Stadt" …).
 */
export default function InfoSection({
  icon,
  eyebrow,
  title,
  children,
}: {
  icon: InfoSectionIcon;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  const Icon = ICONS[icon];
  return (
    <motion.section
      variants={stagger(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-label={title}
    >
      <motion.p
        variants={fadeUp}
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-accent mb-3"
      >
        <Icon size={14} aria-hidden="true" />
        {eyebrow}
      </motion.p>
      <motion.h2
        variants={fadeUp}
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-5"
      >
        {title}
      </motion.h2>
      <motion.div variants={fadeUp} className="flex flex-col gap-4 text-ink-soft leading-relaxed">
        {children}
      </motion.div>
    </motion.section>
  );
}
